import { ServiceType } from '@uipath/portal-shell-util';

import {
    LanguageCodes,
    Scope,
} from '../constants/constants';
import { isMsiOrStandalone } from './EnvironmentUtil';
import { GlobalPropsInstanceResolver } from './globalProps/globalPropsInstanceResolver';
import Logger from './logger/logger';
import { getScopedUrl } from './menu';
import {
    getOrganizationStandardUrl,
    parseOrganizationStandardUrl,
} from './orgStandardUrlHelper';
import { getTenantName } from './urlHelper';

export function isFalsyOrWhitespace(str: string | null | undefined = '') {
    return !str?.trim();
}

export function parseArrayProps(prop: string): string[] {
    return prop?.split?.(',').map(s => s.toLowerCase().trim()) ?? [];
}

export function caseInsensitiveCompare(str1: string, str2: string) {
    return str1.toLowerCase() === str2.toLowerCase();
}

export function isDefined<T>(value: T | null | undefined): value is T {
    return typeof value !== 'undefined' && value != null;
}

export const isString = (value: any): value is string => {
    return typeof value === 'string';
};

export const isNonEmptyString = (value: string | null | undefined): value is string => {
    return isString(value) && value.length > 0;
};

export const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (_, value) => {
        if (typeof value === 'object' && value != null) {
            if (seen.has(value)) {
                return;
            }
            seen.add(value);
        }
        return value;
    };
};

export const getScopedUrlFromDevProps = (serviceType: ServiceType, scope: Scope, tenant?: string) => {
    const isStandAlone = !!isMsiOrStandalone();
    const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
    const tenantName = tenant ?? GlobalPropsInstanceResolver.Instance?.devProperties?.tenant ?? getTenantName() ?? '';
    const { baseUrl } = parseOrganizationStandardUrl(organizationStandardUrl ?? '');
    const standardUrl = isStandAlone ? baseUrl : organizationStandardUrl;
    return getScopedUrl(
        standardUrl,
        tenantName,
        serviceType,
        scope,
        isStandAlone);
};

/**
 * `parseOrReturn(item, fallback)` helps in the case where `item` could be a valid object _or_ a string that should be parsed.
 * This is a common case with the Stencil property API.
 *
 * - If `item` is not a string, `item` will be returned
 * - If `item` is a string, and it is successfully parsed, the parsed object will be returned
 * - If `item` is a string, and parse fails, the `fallback` item will be returned.
 */
export const parseOrReturn = <T>(item: T | string, fallback: T) => {
    if (typeof item === 'string') {
        try {
            return JSON.parse(item);
        } catch (err) {
            Logger.error('utils', `Error parsing ${item}: ${err}`, { printToConsoleOnly: true });
            return fallback;
        }
    }

    return item;
};

/**
 * Helper function to sanitize a token for logs
 */
export const sanitizeToken = (token?: string | null): string => {
    if (!!token && !isString(token)) {
        return '<invalid>';
    }
    return isFalsyOrWhitespace(token) ? '<empty>' : '<token>';
};

export const sanitizePii = (pii?: string | null): string => {
    return isFalsyOrWhitespace(pii) ? '<empty>' : '<pii>';
};

export const sanitizeEmail = (message?: string | null): string => {
    if (!message || !isString(message)) {
        return '<invalid>';
    }
    // eslint-disable-next-line spaced-comment
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g; //NOSONAR email is from error message
    return message.replace(emailRegex, '<email-pii>');
};

export const removeObjectUndefinedProperties = (obj: any) => {
    const tempObj = { ...obj };
    Object.entries(tempObj).forEach(([ key, value ]) => {
        if (value == null) {
            delete tempObj[key];
        }
    });
    return tempObj;
};

export const translatedContactSalesUrl = (locale: string) => {
    switch (locale.toLowerCase()) {
        case LanguageCodes.zhCn:
            return 'https://www.uipath.com.cn/company/contact-us/';
        case LanguageCodes.ja:
        case LanguageCodes.de:
        case LanguageCodes.fr:
        case LanguageCodes.ko:
        case LanguageCodes.ru:
        case LanguageCodes.tr:
            return 'https://www.uipath.com/' + locale + '/company/contact-us';
        case LanguageCodes.es:
        case LanguageCodes.esMx:
            return 'https://www.uipath.com/es/company/contact-us';
        case LanguageCodes.pt:
        case LanguageCodes.ptBr:
            return 'https://www.uipath.com/pt/company/contact-us';
        default:
            return 'https://www.uipath.com/company/contact-us';
    }
};
